import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  alpha,
  IconButton,
  Slider,
  Tooltip,
  Divider,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  PlayArrowRounded,
  PauseRounded,
  SkipNextRounded,
  SkipPreviousRounded,
  RefreshRounded,
  RestartAltRounded,
  Brightness6Rounded,
  VolumeUpRounded,
  ScreenRotationRounded,
  ScheduleRounded,
  CloseRounded,
  ViewCarouselRounded,
  MonitorHeartRounded,
  WifiTetheringRounded,
  LayersRounded,
  SaveRounded,
} from '@mui/icons-material';
import api, { errorMessage } from '../lib/api';
import { relativeTime } from '../lib/format';
import { palette } from '../theme';
import { StatusPill, EmptyState } from '../components/ui/GlassPanel';
import { useToast } from '../components/ui/ToastProvider';

const DEFAULT_SCHEDULE = [
  { id: 1, start: '06:00', end: '10:00', content: 'Morning Loop', days: 'Mon–Fri', enabled: true },
  { id: 2, start: '10:00', end: '16:00', content: 'Corporate Deck', days: 'Mon–Fri', enabled: true },
  { id: 3, start: '16:00', end: '22:00', content: 'Evening Promo', days: 'Mon–Sun', enabled: true },
];

function HoloRing({ color = palette.primary, size = 220, speed = 9 }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        width: size,
        height: size,
        borderRadius: '50%',
        border: `1px solid ${alpha(color, 0.32)}`,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        animation: `holoSpin ${speed}s linear infinite`,
        pointerEvents: 'none',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '12%',
          borderRadius: '50%',
          border: `1px solid ${alpha(color, 0.22)}`,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: '26%',
          borderRadius: '50%',
          border: `1px solid ${alpha(color, 0.16)}`,
        },
      }}
    />
  );
}

function FloatingControl({ icon, label, onClick, active, accent = palette.primary, disabled }) {
  return (
    <Tooltip title={label}>
      <Box
        component="button"
        onClick={onClick}
        disabled={disabled}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 68,
          px: 1.25,
          py: 1.1,
          borderRadius: 3,
          cursor: disabled ? 'not-allowed' : 'pointer',
          border: `1px solid ${alpha(active ? accent : palette.primary, active ? 0.5 : 0.16)}`,
          background: `linear-gradient(160deg, ${alpha(active ? accent : '#0C1428', active ? 0.22 : 0.82)}, ${alpha('#050816', 0.7)})`,
          color: active ? accent : 'text.secondary',
          backdropFilter: 'blur(18px)',
          boxShadow: active ? `0 14px 34px -18px ${alpha(accent, 0.95)}` : '0 12px 28px -20px rgba(0,0,0,0.9)',
          opacity: disabled ? 0.45 : 1,
          transition: 'transform 220ms cubic-bezier(0.22,0.9,0.28,1), border-color 220ms ease, color 220ms ease',
          fontFamily: 'inherit',
          '&:hover': disabled
            ? {}
            : {
                transform: 'translateY(-3px)',
                color: accent,
                borderColor: alpha(accent, 0.45),
              },
        }}
      >
        {icon}
        <Typography sx={{ fontSize: '0.6rem', letterSpacing: '0.08em', fontWeight: 600 }}>{label}</Typography>
      </Box>
    </Tooltip>
  );
}

/**
 * Dedicated signage visualisation.
 *
 * This view is rendered exclusively for the selected signage endpoint - every
 * other device keeps the standard IoT interface.
 */
export default function SignageMode({ device, onExit }) {
  const toast = useToast();
  const [state, setState] = useState(null);
  const [media, setMedia] = useState([]);
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [loading, setLoading] = useState(true);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [brightness, setBrightness] = useState(80);
  const [volume, setVolume] = useState(45);
  const [orientation, setOrientation] = useState('landscape');
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [stateResponse, mediaResponse] = await Promise.allSettled([
        api.get(`/signage/device/${device.device_id}/state`),
        api.get('/signage/images', { params: { device_id: device.device_id } }),
      ]);

      if (stateResponse.status === 'fulfilled') {
        const payload = stateResponse.value?.data || {};
        setState(payload);
        setBrightness(payload.brightness ?? 80);
        setVolume(payload.volume ?? 45);
        setOrientation(payload.orientation ?? 'landscape');
        if (payload.schedule?.length) {
          setSchedule(payload.schedule);
        }
      }

      if (mediaResponse.status === 'fulfilled') {
        setMedia(mediaResponse.value?.data || []);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('signage state', error);
    } finally {
      setLoading(false);
    }
  }, [device.device_id]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 20000);
    return () => clearInterval(interval);
  }, [load]);

  const control = useCallback(
    async (action, value) => {
      try {
        const response = await api.post(`/signage/device/${device.device_id}/control`, { action, value });
        const config = response.data?.signage;
        if (config) {
          setState((current) => ({ ...current, ...config }));
        }
        toast.success(`${action.charAt(0).toUpperCase()}${action.slice(1)} applied to the signage endpoint.`);
      } catch (error) {
        toast.error(errorMessage(error, 'The signage command could not be applied.'));
      }
    },
    [device.device_id, toast]
  );

  const currentMedia = useMemo(() => {
    const index = state?.current_index ?? 0;
    return media[index] || media[0] || null;
  }, [media, state?.current_index]);

  useEffect(() => {
    if (state?.playback !== 'playing' || !currentMedia) return undefined;
    const duration = Math.max(3, (currentMedia.duration_seconds || 10) * 1000);
    timerRef.current = setTimeout(() => control('next'), duration);
    return () => clearTimeout(timerRef.current);
  }, [state?.playback, currentMedia, control, state?.current_index]);

  const saveSchedule = async () => {
    try {
      await api.post(`/signage/device/${device.device_id}/schedule`, { schedule });
      toast.success('Playback schedule synchronised with the endpoint.');
      setScheduleOpen(false);
    } catch (error) {
      toast.error(errorMessage(error, 'The schedule could not be saved.'));
    }
  };

  const brightnessFilter = 0.35 + (brightness / 100) * 0.75;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        overflowY: 'auto',
        background: `radial-gradient(1200px 800px at 50% -10%, ${alpha(palette.secondary, 0.18)}, transparent 60%),
                     radial-gradient(900px 700px at 15% 110%, ${alpha(palette.primary, 0.16)}, transparent 60%),
                     ${palette.background}`,
      }}
    >
      <Box className="holo-grid" sx={{ position: 'absolute', inset: 0, opacity: 0.45 }} />

      {/* header */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: { xs: 2, md: 4 },
          py: 2,
          borderBottom: `1px solid ${alpha(palette.primary, 0.14)}`,
          backdropFilter: 'blur(20px)',
          background: alpha('#050816', 0.55),
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2.25,
            display: 'grid',
            placeItems: 'center',
            background: alpha(palette.secondary, 0.16),
            border: `1px solid ${alpha(palette.secondary, 0.35)}`,
            boxShadow: `0 0 24px ${alpha(palette.secondary, 0.4)}`,
          }}
        >
          <ViewCarouselRounded sx={{ color: palette.secondary, fontSize: 20 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.62rem', letterSpacing: '0.22em', color: palette.secondary }}>
            SIGNAGE MODE
          </Typography>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
            {(device.properties || {}).device_name || device.device_id}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <StatusPill
          label={state?.status || device.status}
          tone={state?.status === 'online' ? 'success' : 'danger'}
        />
        <Chip
          size="small"
          label={`Brightness ${brightness}%`}
          sx={{ color: 'text.secondary', background: alpha('#334155', 0.3), fontSize: '0.68rem' }}
        />
        <Chip
          size="small"
          label={`Orientation ${orientation}`}
          sx={{ color: 'text.secondary', background: alpha('#334155', 0.3), fontSize: '0.68rem' }}
        />
        <Tooltip title="Exit signage mode">
          <IconButton onClick={onExit} sx={{ color: 'text.secondary', border: `1px solid ${alpha(palette.primary, 0.18)}` }}>
            <CloseRounded />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ position: 'relative', zIndex: 1, px: { xs: 2, md: 4 }, py: { xs: 2.5, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2.1fr) minmax(320px, 1fr)' } }}>
          {/* immersive content canvas */}
          <Box
            sx={{
              position: 'relative',
              minHeight: { xs: 320, md: 540 },
              borderRadius: 5,
              overflow: 'hidden',
              border: `1px solid ${alpha(palette.secondary, 0.28)}`,
              background: `linear-gradient(160deg, ${alpha('#0D1630', 0.9)}, ${alpha('#04070F', 0.95)})`,
              boxShadow: `0 46px 100px -44px rgba(0,0,0,0.95), inset 0 1px 0 ${alpha('#FFFFFF', 0.06)}`,
            }}
          >
            <HoloRing color={palette.secondary} size={340} speed={12} />
            <HoloRing color={palette.primary} size={210} speed={8} />

            <Box className="holo-scan" sx={{ position: 'absolute', inset: 0, opacity: 0.8 }} />

            {currentMedia ? (
              <Box
                sx={{
                  position: 'absolute',
                  inset: { xs: 24, md: 48 },
                  display: 'grid',
                  placeItems: 'center',
                  transform: `perspective(1400px) rotateY(${orientation === 'portrait' ? '12deg' : '-4deg'}) rotateX(${orientation === 'portrait' ? '-6deg' : '4deg'})`,
                  transition: 'transform 700ms cubic-bezier(0.22,0.9,0.28,1)',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: `1px solid ${alpha(palette.primary, 0.28)}`,
                    boxShadow: `0 30px 80px -30px ${alpha(palette.primary, 0.8)}`,
                    filter: `brightness(${brightnessFilter}) saturate(1.05)`,
                    transition: 'filter 500ms ease',
                  }}
                >
                  <Box
                    component="img"
                    src={currentMedia.download_url}
                    alt={currentMedia.original_name}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                <EmptyState
                  icon={<LayersRounded />}
                  title="No media assigned"
                  description="Upload signage content from the Signage Media console to populate this display."
                  minHeight={260}
                />
              </Box>
            )}

            {/* holographic overlay */}
            <Box
              sx={{
                position: 'absolute',
                left: 24,
                bottom: 22,
                right: 24,
                display: 'flex',
                alignItems: 'flex-end',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <Typography sx={{ fontSize: '0.62rem', letterSpacing: '0.2em', color: alpha(palette.secondary, 0.9) }}>
                  NOW PLAYING
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>
                  {currentMedia?.original_name || 'No active content'}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {currentMedia
                    ? `${currentMedia.duration_seconds}s dwell · slot ${(state?.current_index ?? 0) + 1} of ${media.length}`
                    : 'Awaiting media distribution'}
                </Typography>
              </Box>
              <StatusPill
                label={state?.playback === 'playing' ? 'playing' : 'paused'}
                tone={state?.playback === 'playing' ? 'success' : 'warning'}
              />
            </Box>
          </Box>

          {/* control column */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 4,
                border: `1px solid ${alpha(palette.primary, 0.14)}`,
                background: `linear-gradient(160deg, ${alpha('#0C1428', 0.86)}, ${alpha('#050816', 0.9)})`,
                backdropFilter: 'blur(22px)',
              }}
            >
              <Typography sx={{ fontSize: '0.66rem', letterSpacing: '0.2em', color: 'text.secondary', mb: 2 }}>
                PLAYBACK CONTROL
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                <FloatingControl
                  icon={<SkipPreviousRounded sx={{ fontSize: 19 }} />}
                  label="Previous"
                  onClick={() => control('previous')}
                />
                <FloatingControl
                  icon={
                    state?.playback === 'playing' ? (
                      <PauseRounded sx={{ fontSize: 19 }} />
                    ) : (
                      <PlayArrowRounded sx={{ fontSize: 19 }} />
                    )
                  }
                  label={state?.playback === 'playing' ? 'Pause' : 'Play'}
                  accent={palette.success}
                  active={state?.playback === 'playing'}
                  onClick={() => control(state?.playback === 'playing' ? 'pause' : 'play')}
                />
                <FloatingControl
                  icon={<SkipNextRounded sx={{ fontSize: 19 }} />}
                  label="Next"
                  onClick={() => control('next')}
                />
                <FloatingControl
                  icon={<RefreshRounded sx={{ fontSize: 18 }} />}
                  label="Refresh"
                  onClick={() => control('refresh')}
                />
                <FloatingControl
                  icon={<RestartAltRounded sx={{ fontSize: 18 }} />}
                  label="Restart"
                  accent={palette.warning}
                  onClick={() => control('restart')}
                />
                <FloatingControl
                  icon={<ScheduleRounded sx={{ fontSize: 18 }} />}
                  label="Schedule"
                  accent={palette.secondary}
                  active={scheduleOpen}
                  onClick={() => setScheduleOpen((current) => !current)}
                />
              </Box>
            </Box>

            <Box
              sx={{
                p: 2.5,
                borderRadius: 4,
                border: `1px solid ${alpha(palette.primary, 0.14)}`,
                background: `linear-gradient(160deg, ${alpha('#0C1428', 0.86)}, ${alpha('#050816', 0.9)})`,
                backdropFilter: 'blur(22px)',
              }}
            >
              <Typography sx={{ fontSize: '0.66rem', letterSpacing: '0.2em', color: 'text.secondary', mb: 1.75 }}>
                DISPLAY CONTROL
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                <Brightness6Rounded sx={{ fontSize: 17, color: palette.warning }} />
                <Typography sx={{ fontSize: '0.74rem', flex: 1 }}>Screen brightness</Typography>
                <Typography className="mono" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                  {brightness}%
                </Typography>
              </Box>
              <Slider
                size="small"
                value={brightness}
                min={5}
                max={100}
                onChange={(_, value) => setBrightness(value)}
                onChangeCommitted={(_, value) => control('brightness', value)}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 1.5, mb: 1 }}>
                <VolumeUpRounded sx={{ fontSize: 17, color: palette.primary }} />
                <Typography sx={{ fontSize: '0.74rem', flex: 1 }}>Volume</Typography>
                <Typography className="mono" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                  {volume}%
                </Typography>
              </Box>
              <Slider
                size="small"
                value={volume}
                min={0}
                max={100}
                onChange={(_, value) => setVolume(value)}
                onChangeCommitted={(_, value) => control('volume', value)}
              />

              <Divider sx={{ borderColor: alpha(palette.primary, 0.1), my: 1.75 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                <ScreenRotationRounded sx={{ fontSize: 17, color: palette.secondary }} />
                <Typography sx={{ fontSize: '0.74rem', flex: 1 }}>Display orientation</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {['landscape', 'portrait'].map((option) => (
                  <Button
                    key={option}
                    size="small"
                    variant={orientation === option ? 'contained' : 'outlined'}
                    onClick={() => {
                      setOrientation(option);
                      control('orientation', option);
                    }}
                    sx={{ flex: 1, fontSize: '0.72rem' }}
                  >
                    {option}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* device telemetry */}
            <Box
              sx={{
                p: 2.5,
                borderRadius: 4,
                border: `1px solid ${alpha(palette.primary, 0.14)}`,
                background: `linear-gradient(160deg, ${alpha('#0C1428', 0.86)}, ${alpha('#050816', 0.9)})`,
                backdropFilter: 'blur(22px)',
                display: 'grid',
                gap: 1.4,
              }}
            >
              <Typography sx={{ fontSize: '0.66rem', letterSpacing: '0.2em', color: 'text.secondary' }}>
                ENDPOINT STATUS
              </Typography>
              {[
                {
                  icon: <MonitorHeartRounded sx={{ fontSize: 16 }} />,
                  label: 'Health status',
                  value: state?.health || 'unknown',
                  tone: state?.health === 'optimal' ? 'success' : 'warning',
                },
                {
                  icon: <WifiTetheringRounded sx={{ fontSize: 16 }} />,
                  label: 'Connectivity',
                  value: state?.status === 'online' ? 'linked' : 'unlinked',
                  tone: state?.status === 'online' ? 'success' : 'danger',
                },
                {
                  icon: <LayersRounded sx={{ fontSize: 16 }} />,
                  label: 'Media library',
                  value: `${state?.media_count ?? media.length} assets`,
                  tone: 'info',
                },
                {
                  icon: <RefreshRounded sx={{ fontSize: 16 }} />,
                  label: 'Last heartbeat',
                  value: relativeTime(state?.last_seen || device.last_seen),
                  tone: 'neutral',
                },
              ].map((row) => (
                <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                  <Box sx={{ color: 'text.secondary', display: 'flex' }}>{row.icon}</Box>
                  <Typography sx={{ fontSize: '0.75rem', flex: 1, color: 'text.secondary' }}>{row.label}</Typography>
                  <StatusPill label={row.value} tone={row.tone} pulse={false} />
                </Box>
              ))}
            </Box>

            {/* schedule */}
            {scheduleOpen && (
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  border: `1px solid ${alpha(palette.secondary, 0.25)}`,
                  background: `linear-gradient(160deg, ${alpha('#0C1428', 0.9)}, ${alpha('#050816', 0.92)})`,
                  backdropFilter: 'blur(22px)',
                  animation: 'holoFloat 260ms ease',
                }}
              >
                <Typography sx={{ fontSize: '0.66rem', letterSpacing: '0.2em', color: 'text.secondary', mb: 1.5 }}>
                  PLAYBACK SCHEDULE
                </Typography>
                <Box sx={{ display: 'grid', gap: 1 }}>
                  {schedule.map((entry) => (
                    <Box
                      key={entry.id}
                      sx={{
                        p: 1.25,
                        borderRadius: 2.5,
                        border: `1px solid ${alpha(palette.primary, 0.12)}`,
                        background: alpha(palette.primary, 0.05),
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{entry.content}</Typography>
                        <Typography className="mono" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                          {entry.start} – {entry.end} · {entry.days}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={Boolean(entry.enabled)}
                            onChange={(event) =>
                              setSchedule((current) =>
                                current.map((item) =>
                                  item.id === entry.id ? { ...item, enabled: event.target.checked } : item
                                )
                              )
                            }
                          />
                        }
                        sx={{ mr: 0 }}
                      />
                    </Box>
                  ))}
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SaveRounded sx={{ fontSize: 16 }} />}
                  onClick={saveSchedule}
                  sx={{ mt: 1.75 }}
                >
                  Synchronise schedule
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {loading && (
          <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '0.7rem', letterSpacing: '0.2em', color: 'text.secondary' }}>
            SYNCHRONISING SIGNAGE ENDPOINT…
          </Typography>
        )}
      </Box>
    </Box>
  );
}
